import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MatchViewer from '@/components/match/MatchViewer';
import ErrorDisplay from '@/components/shared/ErrorDisplay';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { useMatchData } from '@/hooks/useMatchData';
import { getVideoStatus } from '@/api/videoApi';
import { useQuery } from '@tanstack/react-query';

/**
 * Pagina di dettaglio di una partita.
 * Carica i risultati tramite polling e mostra il MatchViewer.
 * Se i risultati non sono ancora pronti, mostra lo stato di elaborazione.
 */
const MatchPage: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();

  // Stato del video (per mostrare messaggi di elaborazione)
  const videoStatusQuery = useQuery({
    queryKey: ['videoStatus', videoId],
    queryFn: () => getVideoStatus(videoId!),
    enabled: !!videoId,
    refetchInterval: 5000,
  });

  // Risultati e dati derivati
  const { results, isLoading, error, refetch } = useMatchData(videoId ?? null);

  const status = videoStatusQuery.data?.status;
  const isProcessing = status && status !== 'completed' && status !== 'failed';

  // URL per il player video: proxiamo attraverso il backend
  const videoUrl = videoId ? `/api/videos/${videoId}/stream` : '';

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}>
      {/* Breadcrumb / navigazione */}
      <div style={{ marginBottom: '1rem' }}>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none',
            border: 'none',
            color: '#3b82f6',
            cursor: 'pointer',
            fontSize: '0.9rem',
            padding: 0,
            textDecoration: 'underline',
          }}
        >
          ← Torna alla Home
        </button>
      </div>

      <h1 style={{ color: '#1e293b', marginBottom: '1.5rem' }}>
        Analisi Partita
      </h1>

      {/* Stato di elaborazione */}
      {isProcessing && (
        <div
          style={{
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            textAlign: 'center',
            color: '#1e40af',
          }}
        >
          <LoadingSpinner />
          <p style={{ marginTop: '0.75rem', fontWeight: 500 }}>
            {status === 'queued' && 'Video in coda per l’elaborazione…'}
            {status === 'preprocessing' && 'Pre-processing del video in corso…'}
            {status === 'detecting' && 'Rilevamento giocatori e palla…'}
            {status === 'tracking' && 'Tracciamento dei giocatori…'}
            {status === 'action_spotting' && 'Identificazione delle azioni…'}
            {status === 'assembling' && 'Assemblaggio dei rally…'}
            {status === 'postprocessing' && 'Generazione clip e statistiche…'}
            {!status && 'Elaborazione in corso…'}
          </p>
          <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.5rem' }}>
            Questa pagina si aggiornerà automaticamente al termine dell'elaborazione.
          </p>
        </div>
      )}

      {/* Errore elaborazione */}
      {status === 'failed' && (
        <div
          style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            color: '#b91c1c',
          }}
        >
          <p style={{ fontWeight: 600 }}>Elaborazione fallita</p>
          <p style={{ fontSize: '0.85rem' }}>
            {videoStatusQuery.data?.error_message || 'Si è verificato un errore durante l’analisi del video.'}
          </p>
        </div>
      )}

      {/* Errore di caricamento risultati */}
      {error && !isProcessing && (
        <ErrorDisplay
          message={`Errore nel caricamento dei risultati: ${error}`}
          onRetry={() => refetch()}
        />
      )}

      {/* Loading spinner per risultati (quando il video è completato ma i risultati non sono ancora nel DB) */}
      {isLoading && !isProcessing && !error && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
          <LoadingSpinner />
          <p style={{ marginTop: '0.5rem' }}>Caricamento risultati…</p>
        </div>
      )}

      {/* MatchViewer con i risultati */}
      {results && !isLoading && (
        <MatchViewer
          results={results}
          videoUrl={videoUrl}
          isLoading={false}
          error={null}
          onRetry={() => refetch()}
        />
      )}

      {/* Nessun risultato e nessun processamento: video non trovato o senza job */}
      {!results && !isLoading && !isProcessing && !error && status === 'completed' && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
          Nessun risultato disponibile per questo video.
        </div>
      )}

      {!results && !isLoading && !isProcessing && !error && !status && !videoStatusQuery.isLoading && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
          Video non trovato o non ancora elaborato.
        </div>
      )}
    </div>
  );
};

export default MatchPage;